import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BottomSheet } from '@astryxdesign/core/BottomSheet';
import { useToast } from '@astryxdesign/core/Toast';
import { ArrowLeft, CaretDown, CaretLeft, CaretRight, Check, X } from '@phosphor-icons/react';
import { api } from '@/lib/api';
import { AppError, errorText } from '@/lib/errors';
import { addDays, dialCode, duration, fmt, fmtDate, humanDate, localDate, priceLabel } from '@/lib/format';
import { loadDraft, saveBooking, saveDraft } from '@/lib/storage';
import type { BookingResult } from '@/lib/types';
import { contactSchema, fieldErrors, shortPhone, typePhone } from '@/lib/validation';
import { useIdempotencyKey } from '@/components/ui';
import { availabilityKey } from '@/features/booking/SlotPicker';
import { useSlug, useStudio } from '@/features/studio/StudioRoot';
import { BOOKINGS_CHANGED } from '@/features/studio/StudioLayout';
import { serviceLabels } from './labels';

const WINDOW = 14;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Preselection from the link (`?service=…&master=…&start=…`). */
  serviceId: string | null;
  masterId: string | null;
  start: string | null;
}

/**
 * One-screen booking: service, day, time and contacts in a single sheet. Its state lives here,
 * outside the sheet content, so closing and reopening the sheet keeps what the client entered.
 */
export function BrightBooking({ isOpen, onClose, serviceId, masterId, start }: Props) {
  const slug = useSlug();
  const { tenant, services, resources } = useStudio();
  const tz = tenant.timezone;
  const code = dialCode(tenant.currency);
  const toast = useToast();
  const qc = useQueryClient();
  const labels = useMemo(() => serviceLabels(services), [services]);
  const popular = useMemo(() => {
    const p = services.filter((s) => s.popular);
    return p.length ? p : services.slice(0, 6);
  }, [services]);
  const categories = useMemo(() => [...new Set(services.map((s) => s.category || 'Услуги'))], [services]);
  const today = localDate(new Date(), tz);

  const [pickedService, setPickedService] = useState<string | null>(null);
  const [masterPick, setMasterPick] = useState<string | null>(null);
  const [windowStart, setWindowStart] = useState(today);
  const [day, setDay] = useState<string | null>(null);
  const [time, setTime] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [allCategory, setAllCategory] = useState(categories[0] ?? '');
  const [contact, setContact] = useState(() => ({ ...loadDraft(slug), notes: '' }));
  const [touched, setTouched] = useState(false);
  const [done, setDone] = useState<{ result: BookingResult; stored: boolean } | null>(null);

  // A link that opens the sheet with a service (or a time) overrides the last pick.
  const [openedWith, setOpenedWith] = useState('');
  const openKey = isOpen ? `${serviceId}|${masterId}|${start}` : '';
  if (openKey !== openedWith) {
    setOpenedWith(openKey);
    if (isOpen && serviceId && services.some((s) => s.id === serviceId)) {
      setPickedService(serviceId);
      setMasterPick(masterId);
      setDay(start ? localDate(new Date(start), tz) : null);
      setTime(start);
      setShowAll(false);
    }
    if (isOpen) setDone(null);
  }

  const service = services.find((s) => s.id === pickedService) ?? popular[0] ?? services[0]!;
  const masters = resources.filter((r) => service.resource_ids.includes(r.id));
  const master = masters.find((m) => m.id === masterPick) ?? null;
  const chips = popular.some((s) => s.id === service.id) ? popular : [service, ...popular];

  const q = useQuery({
    queryKey: availabilityKey(slug, service.id, master?.id ?? null, windowStart, WINDOW),
    queryFn: () => api.availability(slug, service.id, master?.id ?? null, windowStart, WINDOW),
    enabled: isOpen,
    staleTime: 20_000,
    refetchInterval: isOpen ? 60_000 : false,
  });
  const days = useMemo(() => (q.data?.days ?? []).map((d) => ({ ...d, free: d.is_open ? d.slots.filter((s) => s.free) : [] })), [q.data]);
  // The chosen day and time stay only while they are still free; otherwise fall back to the first free day.
  const current = days.find((d) => d.date === day && d.free.length) ?? days.find((d) => d.free.length) ?? null;
  const chosen = current?.free.some((s) => s.start === time) ? time : null;
  const canPrev = windowStart > today;
  const canNext = addDays(windowStart, WINDOW) <= addDays(today, tenant.horizon_days);

  const pickService = (id: string) => {
    setPickedService(id);
    setTime(null);
    setShowAll(false);
  };
  const shiftWindow = (by: number) => {
    setWindowStart(addDays(windowStart, by));
    setDay(null);
    setTime(null);
  };

  const errors: Partial<Record<string, string>> = touched ? { ...fieldErrors(contactSchema, contact) } : {};
  if (touched && !errors.phone && shortPhone(contact.phone)) errors.phone = shortPhone(contact.phone);
  const idemKey = useIdempotencyKey([service.id, master?.id ?? null, chosen, contact.phone.replace(/\D/g, '')]);

  const book = useMutation({
    mutationFn: async () => {
      const parsed = contactSchema.safeParse(contact);
      if (!parsed.success || !chosen) throw new AppError('validation', 'Проверьте имя и телефон');
      return api.createBooking({
        slug,
        serviceId: service.id,
        resourceId: master?.id ?? null,
        startsAt: chosen,
        name: parsed.data.name,
        phone: parsed.data.phone,
        notes: parsed.data.notes,
        idempotencyKey: idemKey,
      });
    },
    onSuccess: (r) => {
      saveDraft(slug, { name: contact.name.trim(), phone: contact.phone.trim() });
      const stored = r.token ? saveBooking(slug, { id: r.booking_id, token: r.token, startsAt: r.starts_at, serviceName: r.service_name, savedAt: new Date().toISOString() }) : false;
      window.dispatchEvent(new Event(BOOKINGS_CHANGED));
      void qc.invalidateQueries({ queryKey: ['availability', slug] });
      setDone({ result: r, stored });
      setTime(null);
      setContact((c) => ({ ...c, notes: '' }));
      setTouched(false);
    },
    onError: (e) => {
      const err = e instanceof AppError ? e : new AppError('unknown');
      if (err.code === 'slot_taken' || err.code === 'slot_invalid') {
        void qc.invalidateQueries({ queryKey: ['availability', slug] });
        toast({ body: err.message, type: 'error' });
        setTime(null);
      }
    },
  });

  const submit = () => {
    setTouched(true);
    if (Object.keys(fieldErrors(contactSchema, contact)).length || shortPhone(contact.phone)) return;
    book.mutate();
  };

  const dayName = (date: string) => humanDate(date, tz);
  const shortDay = (date: string) => (/\d/.test(dayName(date)) ? fmtDate(date, 'EEEEEE d') : dayName(date));
  const fullDay = (iso: string) => {
    const d = dayName(localDate(new Date(iso), tz));
    const text = /\d/.test(d) ? d : `${d}, ${fmt(iso, tz, 'd MMMM')}`;
    return text[0]!.toUpperCase() + text.slice(1);
  };
  const failed = book.isError && !(book.error instanceof AppError && ['slot_taken', 'slot_invalid', 'validation'].includes(book.error.code));

  return (
    <BottomSheet isOpen={isOpen} onOpenChange={(o) => !o && onClose()} label="Запись" height="tall" padding={0}>
      {done ? (
        <section className="br-sheet">
          <header className="br-shead">
            <button type="button" onClick={onClose} aria-label="Закрыть">
              <X size={20} weight="bold" aria-hidden />
            </button>
          </header>
          <section className="br-done">
            <span className="br-done__ok">
              <Check size={42} weight="bold" aria-hidden />
            </span>
            <h3>Вы записаны</h3>
            <p>
              {contact.name.trim().split(' ')[0]}, ждём вас! В записи можно включить напоминание, а если планы поменяются — отменить визит.
            </p>
            <article className="br-ticket">
              <b>{fmt(done.result.starts_at, tz, 'HH:mm')}</b>
              <span>{fullDay(done.result.starts_at)}</span>
              <small>
                {done.result.service_name} · {priceLabel(done.result.price, tenant.currency)}
                {masters.length > 1 && done.result.resource_name ? ` · ${done.result.resource_name}` : ''}
              </small>
            </article>
          </section>
          <footer className="br-sbar">
            <Link
              className="br-btn"
              to={done.stored ? `/s/${slug}/me?b=${done.result.booking_id}&new=1` : `/s/${slug}/b/${done.result.token}?new=1`}
              replace
            >
              Открыть запись
            </Link>
          </footer>
        </section>
      ) : (
        <section className="br-sheet">
          <header className="br-shead">
            <button type="button" onClick={onClose} aria-label="Закрыть">
              <ArrowLeft size={20} weight="bold" aria-hidden />
            </button>
            <h3>Запись</h3>
          </header>

          <section className="br-scroll">
            <p className="br-lab">Услуга</p>
            <span className="br-hs" role="group" aria-label="Услуга">
              {chips.map((s) => (
                <button key={s.id} type="button" className="br-chip" aria-pressed={s.id === service.id} onClick={() => pickService(s.id)}>
                  {labels.get(s.id)?.chip ?? s.name}
                </button>
              ))}
              <button type="button" className="br-chip" aria-expanded={showAll} onClick={() => setShowAll(!showAll)}>
                Все услуги <CaretDown size={14} weight="bold" aria-hidden />
              </button>
            </span>
            {/* Chips carry short names («Комбо L»); what is in the service reads here. */}
            {service.description && !showAll && <p className="br-sdesc">{service.description}</p>}
            {showAll && (
              <section className="br-all">
                {categories.length > 1 && (
                  <span className="br-tabs" role="group" aria-label="Категории">
                    {categories.map((c) => (
                      <button key={c} type="button" className="br-tab" aria-pressed={c === allCategory} onClick={() => setAllCategory(c)}>
                        {c}
                      </button>
                    ))}
                  </span>
                )}
                <ul className="br-list">
                  {services
                    .filter((s) => categories.length < 2 || (s.category || 'Услуги') === allCategory)
                    .map((s) => (
                      <li key={s.id}>
                        <button type="button" className="br-row" aria-pressed={s.id === service.id} onClick={() => pickService(s.id)}>
                          <span>
                            <b>{s.name}</b>
                            {s.description && <span className="br-row__desc">{s.description}</span>}
                            <small>{duration(s.duration_min)}</small>
                          </span>
                          <span className="br-row__price">{priceLabel(s.price, tenant.currency, s.price_is_from)}</span>
                        </button>
                      </li>
                    ))}
                </ul>
              </section>
            )}

            {masters.length > 1 && (
              <>
                <p className="br-lab">Мастер</p>
                <span className="br-hs" role="group" aria-label="Мастер">
                  <button type="button" className="br-chip" aria-pressed={!master} onClick={() => setMasterPick(null)}>
                    Любой свободный
                  </button>
                  {masters.map((m) => (
                    <button key={m.id} type="button" className="br-chip" aria-pressed={master?.id === m.id} onClick={() => setMasterPick(m.id)}>
                      {m.name}
                    </button>
                  ))}
                </span>
              </>
            )}

            <p className="br-lab">День</p>
            <span className="br-hs" role="group" aria-label="День">
              {canPrev && (
                <button type="button" className="br-day" onClick={() => shiftWindow(-WINDOW)} aria-label="Предыдущие две недели">
                  <CaretLeft size={18} weight="bold" aria-hidden />
                </button>
              )}
              {q.isPending
                ? Array.from({ length: 6 }, (_, i) => <span key={i} className="br-day" data-loading aria-hidden />)
                : days.map((d) => (
                    <button
                      key={d.date}
                      type="button"
                      className="br-day"
                      aria-pressed={d.date === current?.date}
                      disabled={!d.free.length}
                      onClick={() => {
                        setDay(d.date);
                        setTime(null);
                      }}
                      aria-label={`${fmtDate(d.date, 'EEEE, d MMMM')}${d.free.length ? '' : ', нет свободного времени'}`}
                    >
                      <small>{d.date === today ? 'сегодня' : fmtDate(d.date, 'EEEEEE')}</small>
                      <b>{fmtDate(d.date, 'd')}</b>
                    </button>
                  ))}
              {canNext && (
                <button type="button" className="br-day" onClick={() => shiftWindow(WINDOW)} aria-label="Следующие две недели">
                  <CaretRight size={18} weight="bold" aria-hidden />
                </button>
              )}
            </span>

            <p className="br-lab">Время{current ? ` · ${dayName(current.date)}` : ''}</p>
            {q.isError ? (
              <p className="br-empty">
                Не удалось загрузить расписание.{' '}
                <button type="button" className="br-link" onClick={() => q.refetch()}>
                  Повторить
                </button>
              </p>
            ) : q.isPending ? (
              <span className="br-times" aria-busy="true" aria-label="Загружаем время">
                {Array.from({ length: 8 }, (_, i) => (
                  <span key={i} className="br-tm" data-loading aria-hidden />
                ))}
              </span>
            ) : current ? (
              <span className="br-times" role="group" aria-label="Время">
                {current.free.map((s) => (
                  <button key={s.start} type="button" className="br-tm" aria-pressed={s.start === chosen} onClick={() => setTime(s.start)}>
                    {fmt(s.start, tz, 'HH:mm')}
                  </button>
                ))}
              </span>
            ) : (
              <p className="br-empty">{canNext ? 'В эти две недели всё занято — посмотрите следующие.' : 'Свободного времени пока нет — напишите нам в WhatsApp.'}</p>
            )}

            <p className="br-lab">Ваши данные</p>
            <input
              className="br-inp"
              placeholder="Имя"
              aria-label="Имя"
              autoComplete="given-name"
              value={contact.name}
              aria-invalid={!!errors.name}
              onChange={(e) => setContact((c) => ({ ...c, name: e.target.value }))}
            />
            {errors.name && <p className="br-err">Напишите, как к вам обращаться</p>}
            <input
              className="br-inp"
              placeholder={code === '996' ? '+996 700 12 34 56' : code === '7' ? '+7 900 123-45-67' : 'Телефон'}
              aria-label="Телефон"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={contact.phone}
              aria-invalid={!!errors.phone}
              onFocus={() => !contact.phone && code && setContact((c) => ({ ...c, phone: `+${code} ` }))}
              onChange={(e) => {
                const phone = typePhone(e.target.value, code);
                setContact((c) => ({ ...c, phone }));
              }}
            />
            {errors.phone && <p className="br-err">{errors.phone}</p>}
            <input
              className="br-inp"
              placeholder="Комментарий (необязательно)"
              aria-label="Комментарий"
              maxLength={500}
              value={contact.notes}
              onChange={(e) => setContact((c) => ({ ...c, notes: e.target.value }))}
            />

            <dl className="br-card br-sum">
              <span>
                <dt>Услуга</dt>
                <dd>{service.name}</dd>
              </span>
              {master && (
                <span>
                  <dt>Мастер</dt>
                  <dd>{master.name}</dd>
                </span>
              )}
              <span>
                <dt>Длится</dt>
                <dd>{duration(service.duration_min)}</dd>
              </span>
              <span>
                <dt>Стоимость</dt>
                <dd>{priceLabel(service.price, tenant.currency, service.price_is_from)}</dd>
              </span>
            </dl>
            {failed && <p className="br-err">Запись не создана: {errorText(book.error)} Нажмите ещё раз — дубля не будет.</p>}
            <p className="br-fine">Нажимая «Записаться», вы соглашаетесь на обработку имени и телефона для этой записи.</p>
          </section>

          <footer className="br-sbar">
            <button type="button" className="br-btn" disabled={!chosen || book.isPending} onClick={submit}>
              {book.isPending ? 'Записываем…' : chosen ? `Записаться · ${shortDay(current!.date)}, ${fmt(chosen, tz, 'HH:mm')}` : 'Выберите время'}
            </button>
            <p className="br-note">Отменить онлайн можно за {tenant.cancel_hours} ч до визита</p>
          </footer>
        </section>
      )}
    </BottomSheet>
  );
}
